/**
 * Client-side quote PDF extraction dispatcher (pdfjs-dist + supplier parsers).
 */

import { logError, logInfo } from '../../config_and_helpers/logger';
import { ENCO_QUOTE_FORMAT_ID } from '../quoteExtractionValidation';
import { extractPdfItemsFromFile } from '../pdf/extractPdfItems';
import { pdfItemsToLines } from '../pdf/pdfItemsToLines';
import {
  buildQuoteExtractionSnapshot,
  RHENIUM_QUOTE_FORMAT_ID,
} from './logQuoteExtraction';
import {
  extractEncoQuote,
  ENCO_PARSER_FORMAT_ID,
} from './parsers/encoQuoteParser';
import {
  extractRheniumQuote,
  RHENIUM_PARSER_FORMAT_ID,
} from './parsers/rheniumQuoteParser';
import { detectSupplierFormat } from './supplierDetection';

const LOG_CONTEXT = 'extractQuoteFromFile';
const MAX_PDF_BYTES = 10 * 1024 * 1024;

/**
 * Merge totals block into quote header for validation and review UI.
 *
 * @param {Object} raw
 * @returns {Object}
 */
export function finalizeRheniumClientPayload(raw, pageCount = null) {
  const quote = { ...(raw.quote || {}) };
  const totals = raw.totals || {};

  if (!quote.currency && totals.currency) {
    quote.currency = totals.currency;
  }

  return {
    ...raw,
    quote,
    meta: {
      ...(raw.meta || {}),
      supplierFormat: RHENIUM_QUOTE_FORMAT_ID,
      parserFormat: RHENIUM_PARSER_FORMAT_ID,
      parserSource: 'react_client_pdfjs',
      parserVersion: raw.meta?.parserVersion || 'rhenium-pdfjs-v1',
      pageCount: pageCount ?? raw.meta?.pageCount ?? null,
    },
  };
}

/**
 * Merge totals into quote header for ENCO review UI and validation.
 *
 * @param {Object} raw
 * @param {number|null} [pageCount]
 * @returns {Object}
 */
export function finalizeEncoClientPayload(raw, pageCount = null) {
  const quote = { ...(raw.quote || {}) };
  const totals = raw.totals || {};
  const supplierContact = raw.supplierContact || {};
  const customer = { ...(raw.customer || {}) };

  if (!quote.agentName && supplierContact.name) {
    quote.agentName = supplierContact.name;
  }
  if (!quote.agentPhone && supplierContact.phone) {
    quote.agentPhone = supplierContact.phone;
  }
  if (!quote.agentEmail && supplierContact.email) {
    quote.agentEmail = supplierContact.email;
  }
  if (!customer.customerNumber && quote.customerNumber) {
    customer.customerNumber = quote.customerNumber;
  }

  if (!quote.currency && totals.currency) {
    quote.currency = totals.currency;
  }
  if (quote.subtotal == null && totals.subtotalBeforeVat != null) {
    quote.subtotal = totals.subtotalBeforeVat;
  }
  if (quote.vatRate == null && totals.vatRate != null) {
    quote.vatRate = totals.vatRate;
  }
  if (quote.vatAmount == null && totals.vatAmount != null) {
    quote.vatAmount = totals.vatAmount;
  }

  return {
    ...raw,
    customer,
    quote,
    meta: {
      ...(raw.meta || {}),
      supplierFormat: ENCO_QUOTE_FORMAT_ID,
      parserFormat: ENCO_PARSER_FORMAT_ID,
      parserSource: 'react_client_pdfjs',
      parserVersion: raw.meta?.parserVersion || 'enco-react-parser-v1',
      pageCount: pageCount ?? raw.meta?.pageCount ?? null,
    },
  };
}

/**
 * Parse a supplier quote PDF in the browser.
 *
 * @param {File} file
 * @returns {Promise<{ success: boolean, data?: Object, error?: string, message?: string }>}
 */
export async function extractQuoteFromFile(file) {
  if (!file || file.type !== 'application/pdf') {
    return {
      success: false,
      error: 'Invalid file type',
      message: 'Only PDF files are supported.',
    };
  }

  if (file.size > MAX_PDF_BYTES) {
    return {
      success: false,
      error: 'File too large',
      message: 'File size must be less than 10MB.',
    };
  }

  logInfo(LOG_CONTEXT, 'extractQuoteFromFile start', {
    fileName: file.name,
    fileSize: file.size,
  });

  try {
    const { pages, pageCount } = await extractPdfItemsFromFile(file);
    const lines = pdfItemsToLines(pages);
    const fullText = lines.map(line => line.text).join('\n');
    const supplierFormat = detectSupplierFormat(fullText);

    logInfo(LOG_CONTEXT, 'pdf text lines preview', {
      fileName: file.name,
      supplierFormat,
      lineCount: lines.length,
      linePreview: lines.slice(0, 40).map((line, index) => ({
        index,
        page: line.page,
        y: line.y,
        text: line.text,
      })),
    });

    if (supplierFormat === ENCO_PARSER_FORMAT_ID) {
      const extracted = extractEncoQuote(lines, file.name);
      const data = finalizeEncoClientPayload(extracted, pageCount);

      logInfo(LOG_CONTEXT, 'extractQuoteFromFile ENCO success', {
        fileName: file.name,
        itemCount: data.items?.length ?? 0,
        quoteNumber: data.quote?.quoteNumber ?? null,
        parserSource: data.meta?.parserSource,
        supplierName: data.supplier?.name ?? null,
        suggestedCustomerNumber: data.customer?.customerNumber ?? null,
        extracted: buildQuoteExtractionSnapshot(data),
      });

      return { success: true, data };
    }

    if (supplierFormat === RHENIUM_PARSER_FORMAT_ID) {
      const extracted = extractRheniumQuote(lines, file.name);
      const data = finalizeRheniumClientPayload(extracted, pageCount);

      logInfo(LOG_CONTEXT, 'extractQuoteFromFile Rhenium success', {
        fileName: file.name,
        itemCount: data.items?.length ?? 0,
        quoteNumber: data.quote?.quoteNumber ?? null,
        parserSource: data.meta?.parserSource,
      });

      return { success: true, data };
    }

    logInfo(LOG_CONTEXT, 'unsupported supplier quote format — client parser skipped', {
      fileName: file.name,
      supplierFormat,
    });

    return {
      success: false,
      error: 'Unsupported format',
      message:
        'This PDF is not recognized as a supported supplier quote. Server extraction may be attempted.',
    };
  } catch (error) {
    logError(LOG_CONTEXT, 'extractQuoteFromFile failed', {
      fileName: file.name,
      message: error?.message,
    });
    return {
      success: false,
      error: error?.message || 'Failed to extract quote from PDF',
      message: error?.message || 'Failed to extract quote from PDF',
    };
  }
}
